import React from 'react';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Box,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Assignment as AssignmentIcon,
    AccessTime as AccessTimeIcon,
    AttachMoney as AttachMoneyIcon,
    BeachAccess as BeachAccessIcon,
    School as SchoolIcon,
    Group as GroupIcon,
    Work as WorkIcon,
    People as PeopleIcon,
    FactCheck as FactCheckIcon,
    EventAvailable as EventAvailableIcon,
    Assessment as AssessmentIcon,
    TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types/auth';

const DRAWER_WIDTH = 240;

const menuItems = [
    { path: '/dashboard', icon: DashboardIcon, label: 'menu.dashboard' },
    { path: '/profile', icon: PersonIcon, label: 'menu.profile' },
    // Área de RH (RH e Diretor): usuários, aprovações e relatórios
    { path: '/colaboradores', icon: BusinessIcon, label: 'menu.company', roles: [UserRole.RH, UserRole.DIRETOR] },
    { path: '/aprovacoes', icon: FactCheckIcon, label: 'menu.approvals', roles: [UserRole.RH, UserRole.DIRETOR] },
    { path: '/ferias-aprovacoes', icon: EventAvailableIcon, label: 'menu.vacationApprovals', roles: [UserRole.RH, UserRole.DIRETOR] },
    { path: '/relatorios', icon: AssessmentIcon, label: 'menu.reports', roles: [UserRole.RH, UserRole.DIRETOR] },
    { path: '/vagas', icon: WorkIcon, label: 'menu.vacancies', roles: [UserRole.RH, UserRole.DIRETOR] },
    { path: '/talent-pool', icon: PeopleIcon, label: 'menu.talentPool', roles: [UserRole.RH, UserRole.DIRETOR] },
    { 
        path: '/gestor/projetos', 
        icon: AssignmentIcon, 
        label: 'menu.projects',
        roles: [UserRole.GESTOR, UserRole.DIRETOR]
    },
    { 
        path: '/gestor/clientes', 
        icon: GroupIcon, 
        label: 'menu.clients',
        roles: [UserRole.GESTOR, UserRole.DIRETOR]
    },
    { path: '/minhas-tarefas', icon: TaskAltIcon, label: 'menu.myTasks' },
    { path: '/horas', icon: AccessTimeIcon, label: 'menu.timeEntries' },
    { path: '/reembolsos', icon: AttachMoneyIcon, label: 'menu.reimbursements' },
    { path: '/ferias', icon: BeachAccessIcon, label: 'menu.vacation' },
    { path: '/pdi', icon: SchoolIcon, label: 'menu.pdi' },
    // "Simulações de Projetos" (/gestor/simulacoes) removido: a rota não existe; simulações ficam dentro de cada projeto
];

interface SidebarProps {
    /** Em telas pequenas o menu é uma gaveta temporária controlada pelo botão ☰ do cabeçalho */
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { user } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleNavigation = (path: string) => {
        if (path === '/dashboard' && [UserRole.GESTOR.toString(), UserRole.DIRETOR.toString()].includes(user?.role || '')) {
            navigate('/gestor/dashboard');
        } else {
            navigate(path);
        }
        if (isMobile) onMobileClose?.();
    };

    const filteredMenuItems = menuItems.filter(item =>
        !item.roles || item.roles.some(role => user?.role === role)
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={onMobileClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: isMobile ? 0 : DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: theme.palette.secondary.main,
                    borderRight: 'none',
                    boxShadow: 'none',
                    mt: isMobile ? 0 : 8,
                    pt: isMobile ? 8 : 0,
                },
            }}
        >
            <Box component="nav" aria-label="Menu principal" sx={{ overflow: 'auto', mt: 1 }}>
                <List>
                    {filteredMenuItems.map((item) => (
                        <ListItemButton
                            key={item.path}
                            onClick={() => handleNavigation(item.path)}
                            selected={location.pathname === item.path || 
                                    (item.path === '/dashboard' && location.pathname === '/gestor/dashboard')}
                            sx={{
                                mb: 0.5,
                                mx: 1,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: theme.palette.primary.light,
                                    },
                                    '& .MuiListItemText-primary': {
                                        color: '#FFFFFF',
                                        fontWeight: 600,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    color: 'rgba(255, 255, 255, 0.7)',
                                }}
                            >
                                <item.icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                                primary={t(item.label)}
                                primaryTypographyProps={{
                                    sx: { 
                                        fontSize: '0.875rem',
                                        color: 'rgba(255, 255, 255, 0.7)' 
                                    }
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Box>
        </Drawer>
    );
};

export default Sidebar; 